import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup, UntypedFormArray } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { debounceTime, filter, tap, takeUntil, switchMap } from 'rxjs/operators';
import { combineLatest, iif, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { IAuroraState } from '@apps/aurora.state';
import * as fromIts2Store from '@apps/its2/store';
import * as fromRootStore from '@apps/root/store';
import * as fromAuthStore from '@apps/auth/store';
import { CreateService, Contact, Step4 } from '@apps/its2/models/create-service';
import { Profile } from '@apps/auth/models/profile';
import { NextParams, StepState } from '@ui/misc/wizard/models';

import { CreateServiceBase } from '../common/create-service-base';

@Component({
    selector: 'aa-create-service-step4',
    templateUrl: './create-service-step4.component.html',
    styleUrls: [
        './create-service-step4.component.scss',
        '../../request-create.scss'
    ],
    standalone: false
})
export class CreateServiceStep4Component extends CreateServiceBase implements OnInit
{
    private static stepIdx: number = 3;
    private profile: Profile;
    private contactList: UntypedFormArray;
    public stepForm: UntypedFormGroup;
    public readonly emailField: string = 'email';
    public readonly firstNameField: string = 'firstName';
    public readonly lastNameField: string = 'lastName';
    public readonly titleField: string = 'title';
    public readonly phoneNumberField: string = 'phoneNumber';
    public readonly mobileField: string = 'mobile';
    public isProcessing: boolean = false;
    private step4: Step4;

    constructor (
        translateService: TranslateService,
        private formBuilder: UntypedFormBuilder,
        private store: Store<IAuroraState>,
        private actions: Actions
    )
    {
        super(translateService);

        this.step4 = new Step4(translateService.currentLang);

        // this.store.dispatch(
        //     fromRootStore.RootMiscStoreActions.RootWizardStepState({
        //         stepIdx: CreateServiceStep4Component.stepIdx,
        //         valid: false
        //     })
        // );

        combineLatest(
            [
                this.store.select(fromAuthStore.getProfileInformations),
                this.store.select(fromIts2Store.getCreateService)
            ])
            .pipe(
                filter(([p,]: [Profile, CreateService]) =>
                    p !== null
                ),
                tap(([p, cs]: [Profile, CreateService]) =>
                {
                    this.profile = p;
                    this.step4 = new Step4(translateService.currentLang, cs.step4);
                    this.createForm(this.step4);
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnInit ()
    {
        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2StepExitRequested),
                filter((nextParams: NextParams) =>
                    nextParams.oldStepIdx === CreateServiceStep4Component.stepIdx
                ),
                switchMap((nextParams: NextParams) =>
                    this.onSubmit()
                        .pipe(
                            switchMap((isValid: boolean) =>
                                iif(
                                    () => isValid,
                                    of(true)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateStep4Exited({ step4: this.step4 })
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep4Component.stepIdx, true)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitAccepted(nextParams)
                                                );
                                            })
                                        ),
                                    of(false)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep4Component.stepIdx, false)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitRefused(nextParams)
                                                );
                                            })
                                        )
                                )
                            )
                        )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    onSubmit = (): Observable<boolean> =>
    {
        this.markControlsDirty(this.stepForm);
        if (this.stepForm.invalid)
        {
            return of(false);
        }

        this.step4 = new Step4(this.translateService.currentLang, this.stepForm.value);
        this.step4.main.email = this.profile.email;
        this.step4.valid = true;

        return of(true);
    };

    onUpdateProfile = () =>
    {
        const p = {};
        const subForm = this.stepForm.get('main');

        [this.firstNameField, this.lastNameField, this.titleField, this.phoneNumberField, this.mobileField].forEach((f) =>
        {
            p[f] = subForm.get(f).value;
        });

        this.store.dispatch(fromAuthStore.AuthProfileStoreActions.AuthProfileInformationsChanged({
            profile: p as Profile
        }));
    };

    public onAddContact (contact: Contact): void
    {
        if (this.contactList.length < 3)
        {
            this.contactList.push(this.newContact(contact));
        }
    }

    public onRemoveContact (idx: number)
    {
        this.contactList.removeAt(idx);
    }

    private createForm (step4: Step4)
    {
        if (!step4.main.email)
        {
            this.stepForm = this.formBuilder.group({
                main: this.formBuilder.group({
                    [this.emailField]: [{ value: this.profile[this.emailField], disabled: true }],
                    [this.firstNameField]: [this.profile[this.firstNameField], Validators.required],
                    [this.lastNameField]: [this.profile[this.lastNameField], Validators.required],
                    [this.titleField]: [this.profile[this.titleField]],
                    [this.phoneNumberField]: [this.profile[this.phoneNumberField]],
                    [this.mobileField]: [this.profile[this.mobileField]]
                }),
                contacts: this.formBuilder.array([])
            });
        }
        else
        {
            this.stepForm = this.formBuilder.group({
                main: this.formBuilder.group({
                    [this.emailField]: [{ value: this.profile[this.emailField], disabled: true }],
                    [this.firstNameField]: [step4.main[this.firstNameField], Validators.required],
                    [this.lastNameField]: [step4.main[this.lastNameField], Validators.required],
                    [this.titleField]: [step4.main[this.titleField]],
                    [this.phoneNumberField]: [step4.main[this.phoneNumberField]],
                    [this.mobileField]: [step4.main[this.mobileField]]
                }),
                contacts: this.formBuilder.array([])
            });
        }

        this.contactList = this.stepForm.controls.contacts as UntypedFormArray;
        step4.contacts.forEach((contact: Contact) =>
            this.contactList.push(this.newContact(contact))
        );

        this.formListener();
    }

    private newContact (contact: Contact)
    {
        if (contact === undefined)
        {
            contact = new Contact(null);
        }

        return this.formBuilder.group({
            [this.emailField]: [contact[this.emailField], Validators.required],
            [this.firstNameField]: [contact[this.firstNameField]],
            [this.lastNameField]: [contact[this.lastNameField]]
        });
    }

    private formListener ()
    {
        this.stepForm.valueChanges
            .pipe(
                debounceTime(250),
                tap(() =>
                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: true })
                    )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
        this.stepForm.statusChanges
            .pipe(
                debounceTime(250),
                tap((result: any) =>
                {
                    if (result === 'VALID')
                    {
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizardStepState({
                                stepIdx: CreateServiceStep4Component.stepIdx,
                                valid: true
                            })
                        );
                    }
                    else
                    {
                        // INVALID, PENDING, or DISABLED
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizardStepState({
                                stepIdx: CreateServiceStep4Component.stepIdx,
                                valid: false
                            })
                        );
                    }
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }
}
